import React, { memo } from 'react'
import { Link } from 'react-router-dom'
import CarrierStatus from 'components/marketplace/CarrierStatus'
import { Avatar } from 'transfix-ui/components/Avatar'
import Card from 'transfix-ui/components/Card'
import Copy from 'transfix-ui/components/Copy/Copy'
import { makeStyles, Typography } from 'transfix-ui/core'
import { ConnectedPhoneNumber } from 'apps/marketplace/components/ConnectedPhoneNumber'
import { EllipsisTooltipText } from 'apps/marketplace/pages/shipment-details/components/EllipsisTooltipText'
import ShipmentCarrierScorecardPopover from 'apps/marketplace/pages/shipment-details/components/ShipmentCarrierScorecardPopover/ShipmentCarrierScorecardPopover'
import { isDigitalOption } from 'apps/marketplace/utils/helpers/isDigitalOption'
import { ICarrierOptionDetailsFragment } from '../../graphql/CarrierOptionDetailsFragment'
import CarrierOptionsCardRow from '../CarrierOptionCardRow/CarrierOptionsCardRow'
import { TEST_IDS } from './constants'
import { getDispatcherEmailSubject } from './utils/getDispatcherEmailSubject'

export interface ICarrierOptionsContactsCardProps {
  option?: ICarrierOptionDetailsFragment
  loading: boolean
}

const useCarrierOptionsContactsStyles = makeStyles(theme => ({
  carrierValue: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
  },
  dispatcher: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  contactName: {
    marginBottom: theme.spacing(0.5),
  },
  email: {
    display: 'flex',
    gap: theme.spacing(1),
    '& > div': {
      padding: '0px !important',
    },
  },
  contacts: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  textRight: {
    [theme.breakpoints.up('lg')]: {
      textAlign: 'end',
      overflowWrap: 'break-word',
    },
    [theme.breakpoints.down('lg')]: {
      flexShrink: 0,
    },
  },
}))

const CarrierOptionsDetailsCard = memo<ICarrierOptionsContactsCardProps>(({ loading, option }) => {
  const classes = useCarrierOptionsContactsStyles()
  const {
    carrierApplication,
    carrierContactName,
    carrierContactPhone,
    carrierContactPhoneExt,
    carrierContactUser,
    createdBy,
    shipment,
    source,
  } = option || {}
  const { carrierManager } = shipment || {}
  const { id: carrierId, primaryCarrierManager, state: carrierState } = carrierApplication || {}

  const { fullName: reporterName, parentUserId: reporterId } = createdBy || {}
  const { fullName: opsRepName, parentUserId: opsRepId } = carrierManager || {}
  const { fullName: pcmName, parentUserId: pcmId } = primaryCarrierManager || {}

  const isDigital = isDigitalOption(source)

  const contact = isDigital ? createdBy : carrierContactUser
  const { email: contactEmail, fullName: contactName, parentUserId: contactId } = contact || {}
  const emailSubject = getDispatcherEmailSubject(shipment)

  return (
    <Card>
      <Typography variant='h3'>Contacts</Typography>

      {/* Carrier Row */}
      <CarrierOptionsCardRow
        title='Carrier'
        value={
          <div className={classes.carrierValue} data-test={TEST_IDS.optionCarrier}>
            <ShipmentCarrierScorecardPopover carrierId={carrierId} />
            <CarrierStatus status={carrierState} />
          </div>
        }
        loading={loading}
      />

      {/* Dispatcher Row */}
      <CarrierOptionsCardRow
        title='Dispatcher'
        value={
          <div className={`${classes.textRight} ${classes.dispatcher}`}>
            {contactId && contactName ? (
              <Typography
                variant='subtitle1'
                component='div'
                className={classes.contactName}
                data-test={TEST_IDS.optionDispatcher}>
                <Link to={`/users/${contactId}`} target='_blank'>
                  <EllipsisTooltipText text={contactName} />
                </Link>
              </Typography>
            ) : (
              <>
                {carrierContactName && (
                  <Typography
                    variant='subtitle1'
                    component='div'
                    className={classes.contactName}
                    data-test={TEST_IDS.optionDispatcher}>
                    <EllipsisTooltipText text={carrierContactName} />
                  </Typography>
                )}
              </>
            )}
            {contactEmail && (
              <div className={classes.email} data-test={TEST_IDS.optionDispatcherEmail}>
                <a
                  href={`mailto:${contactEmail}?subject=${emailSubject}`}
                  target='_blank'
                  rel='noreferrer'>
                  {contactEmail}
                </a>
                <Copy alignClass='top' copyText={contactEmail} useIcon />
              </div>
            )}
            {carrierContactPhone && (
              <div data-test={TEST_IDS.optionDispatcherPhone}>
                <ConnectedPhoneNumber
                  phoneNumber={carrierContactPhone}
                  phoneExtension={carrierContactPhoneExt || undefined}
                  place='left'
                />
              </div>
            )}
          </div>
        }
        loading={loading}
      />

      {/* Reporter Row */}
      <CarrierOptionsCardRow
        title='Reporter'
        value={
          <div className={classes.contacts} data-test={TEST_IDS.optionReporter}>
            <Link to={`/users/${reporterId}`} target='_blank'>
              {reporterName}
            </Link>
            {isDigital && (
              <div data-test={TEST_IDS.optionReporterExternal}>
                <Avatar size='xsmall' tooltipName='Digital Bid' />
              </div>
            )}
          </div>
        }
        loading={loading}
      />

      {/* Ops Rep Row */}
      {opsRepName && (
        <CarrierOptionsCardRow
          title='Ops Rep'
          value={
            <Link to={`/users/${opsRepId}`} target='_blank' data-test={TEST_IDS.optionOpsRep}>
              {opsRepName}
            </Link>
          }
          loading={loading}
        />
      )}

      {/* Primary Carrier Manager Row */}
      {pcmName && (
        <CarrierOptionsCardRow
          title='PCM'
          value={
            <Link to={`/users/${pcmId}`} target='_blank' data-test={TEST_IDS.optionPcm}>
              {pcmName}
            </Link>
          }
          loading={loading}
        />
      )}
    </Card>
  )
})

export default CarrierOptionsDetailsCard
